import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Restaurant } from "../Manager/restaurant.types";
import { fetchSuggestions } from "../../lib/api/restaurant.apis";
import { slugify } from "./Explore/services";

type UseSearchEngineOptions = {
  initialWords?: string[];
  debounceMs?: number;
};

export function useSearchEngine(
  opts: UseSearchEngineOptions = {}
) {
  const navigate = useNavigate();
  const debounceMs = opts.debounceMs ?? 250;
  const initialWords = opts.initialWords ?? [
    "Rooftop",
    "Romantic",
    "Cozy",
    "Seafood",
    "Vegetarian",
    "Brunch",
    "Breakfast",
    "Dinner",
    "Affordable",
  ];

  const [query, setQuery] = useState<string>("");
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [suggestions, setSuggestions] = useState<Array<string | Restaurant>>([]);
  const [loading, setLoading] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem("hg_recent_searches");
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  const timer = useRef<number | null>(null);

  // helpers
  const saveRecent = useCallback(
    (term: string) => {
      try {
        if (!term?.trim()) return;
        setRecentSearches((prev) => {
          const next = [term, ...prev.filter((p) => p !== term)].slice(0, 6);
          localStorage.setItem("hg_recent_searches", JSON.stringify(next));
          return next;
        });
      } catch (e) {}
    },
    [6]
  );

  const toggleFilter = useCallback((filter: string) => {
    setSelectedFilters((prev) => (prev.includes(filter) ? prev.filter((f) => f !== filter) : [...prev, filter]));
  }, []);

  const clearAllFilter = useCallback(() => {
    setSelectedFilters([]);
  }, []);

  useEffect(() => {
    if (timer.current) {
      window.clearTimeout(timer.current);
    }

    // If nothing typed and no filters, show recent + popular words
    if (!query && selectedFilters.length === 0) {
      setSuggestions([...initialWords.slice(0, 4), ...recentSearches]);
      setLoading(false);
      return;
    }

    setLoading(true);

    timer.current = window.setTimeout(async () => {
      try {
        const res = await fetchSuggestions({
          q: query,
          filters: selectedFilters,
          limit: 10, // optional
        });

        const words = res.words ?? [];
        const restaurants = res.restaurants ?? [];

        setSuggestions([...words, ...restaurants]);
      } catch (err) {
        console.error(err);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, debounceMs);

    return () => {
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, [query]);

  // keyboard navigation state
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!suggestions || suggestions.length === 0) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((prev) => {
          const next = prev === null ? 0 : Math.min(prev + 1, suggestions.length - 1);
          return next;
        });
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((prev) => {
          if (prev === null) return suggestions.length - 1;
          return Math.max(prev - 1, 0);
        });
      } else if (e.key === "Enter") {
        e.preventDefault();
        // if a suggestion is active and it's a restaurant -> navigate
        if (activeIndex !== null) {
          const item = suggestions[activeIndex];
          if (typeof item === "string") {
            // fill input
            setQuery(item);
            setActiveIndex(null);
            // keep focus and keep suggestions
          } else {
            // restaurant clicked
            navigateToRestaurant(item);
          }
        } else {
          // general search submit
          submitSearch(query);
        }
      } else if (e.key === "Escape") {
        setIsFocused(false);
      }
    },
    [activeIndex, suggestions, query]
  );

  // navigation handlers
  const navigateToRestaurant = useCallback(
    (r: Restaurant) => {
      saveRecent(r.name);
      setIsFocused(false);
      setQuery("");
      setSuggestions([]);
      navigate(`/restaurant/${r._id}/${slugify(r.name)}`);
    },
    [navigate, saveRecent, slugify]
  );

  const submitSearch = useCallback(
    (term: string, sortBy: string = "relevance") => {
      if (!term?.trim()) return;

      saveRecent(term);
      setIsFocused(false);
      setSuggestions([]);

      // navigate to search results page with query params
      const filtersParam = selectedFilters.length
        ? `&filters=${encodeURIComponent(selectedFilters.join(","))}`
        : "";
      navigate(`/s?q=${encodeURIComponent(term)}${filtersParam}&sortBy=${sortBy}&page=1`);
    },
    [navigate, saveRecent, selectedFilters]
  );

  // click handlers used by UI
  const handleWordClick = useCallback((word: string) => {
    setQuery(word);
    // keep dropdown open
    setIsFocused(true);
  }, []);

  const handleRestaurantClick = useCallback((r: Restaurant) => {
    navigateToRestaurant(r);
  }, [navigateToRestaurant]);

  // clear suggestions on blur after small delay (allow clicks)
  const handleBlur = useCallback(() => {
    setTimeout(() => {
      setIsFocused(false);
      setActiveIndex(null);
    }, 150);
  }, []);

  // expose state & handlers
  return {
    query,
    setQuery,
    isFocused,
    setIsFocused,
    selectedFilters,
    clearAllFilter,
    toggleFilter,
    suggestions,
    loading,
    activeIndex,
    setActiveIndex,
    handleKeyDown,
    handleBlur,
    handleWordClick,
    handleRestaurantClick,
    submitSearch,
    recentSearches,
  };
}
